import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, Clock } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const StaffAvailabilityCalendar = ({ user, token }) => {
  const [unavailabilities, setUnavailabilities] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUnavailability, setNewUnavailability] = useState({
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    reason: '',
    all_day: false
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchUnavailabilities();
  }, []);

  const fetchUnavailabilities = async () => {
    try {
      const response = await fetch(`${API_URL}/api/staff/unavailability`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnavailabilities(data);
      }
    } catch (error) {
      console.error('Error fetching unavailabilities:', error);
    }
  };

  const addUnavailability = async () => {
    if (!newUnavailability.date) {
      alert('Please select a date');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/staff/unavailability`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: newUnavailability.date,
          start_time: newUnavailability.all_day ? null : newUnavailability.start_time,
          end_time: newUnavailability.all_day ? null : newUnavailability.end_time,
          reason: newUnavailability.reason || null,
          all_day: newUnavailability.all_day
        })
      });

      if (response.ok) {
        await fetchUnavailabilities();
        setShowAddForm(false);
        setNewUnavailability({
          date: '',
          start_time: '09:00',
          end_time: '17:00',
          reason: '',
          all_day: false
        });
      } else {
        alert('Failed to add unavailability');
      }
    } catch (error) {
      console.error('Error adding unavailability:', error);
      alert('Error adding unavailability');
    }
  };

  const deleteUnavailability = async (id) => {
    if (!window.confirm('Remove this unavailability?')) return;

    try {
      const response = await fetch(`${API_URL}/api/staff/unavailability/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchUnavailabilities();
      } else {
        alert('Failed to delete unavailability');
      }
    } catch (error) {
      console.error('Error deleting unavailability:', error);
      alert('Error deleting unavailability');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Group by upcoming and past
  const today = new Date().toISOString().split('T')[0];
  const upcoming = unavailabilities.filter(u => u.date >= today);
  const past = unavailabilities.filter(u => u.date < today);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar size={20} />
            My Availability
          </CardTitle>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            size="sm"
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            Mark Unavailable
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showAddForm && (
          <div className="mb-4 p-4 border rounded-lg bg-gray-50">
            <h3 className="font-semibold mb-3">Mark Time Unavailable</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Date</label>
                <Input
                  type="date"
                  value={newUnavailability.date}
                  onChange={(e) => setNewUnavailability({...newUnavailability, date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="all-day"
                  checked={newUnavailability.all_day}
                  onChange={(e) => setNewUnavailability({...newUnavailability, all_day: e.target.checked})}
                  className="rounded"
                />
                <label htmlFor="all-day" className="text-sm">All day unavailable</label>
              </div>

              {!newUnavailability.all_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Start Time</label>
                    <Input
                      type="time"
                      value={newUnavailability.start_time}
                      onChange={(e) => setNewUnavailability({...newUnavailability, start_time: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">End Time</label>
                    <Input
                      type="time"
                      value={newUnavailability.end_time}
                      onChange={(e) => setNewUnavailability({...newUnavailability, end_time: e.target.value})}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Reason (optional)</label>
                <Input
                  placeholder="e.g., Holiday, Personal appointment"
                  value={newUnavailability.reason}
                  onChange={(e) => setNewUnavailability({...newUnavailability, reason: e.target.value})}
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={addUnavailability}>Save</Button>
                <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {upcoming.length === 0 && past.length === 0 && (
          <p className="text-gray-500 text-center py-4">No unavailability marked. You're available for all jobs!</p>
        )}

        {upcoming.length > 0 && (
          <div className="mb-4">
            <h4 className="font-semibold text-sm text-gray-700 mb-2">Upcoming Unavailability</h4>
            <div className="space-y-2">
              {upcoming.map(unavail => (
                <div key={unavail.id} className="flex items-center justify-between p-3 border rounded-lg bg-yellow-50 border-yellow-200">
                  <div>
                    <p className="font-medium">{formatDate(unavail.date)}</p>
                    <p className="text-sm text-gray-600">
                      {unavail.all_day ? (
                        'All day'
                      ) : (
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {formatTime(unavail.start_time)} - {formatTime(unavail.end_time)}
                        </span>
                      )}
                    </p>
                    {unavail.reason && (
                      <p className="text-xs text-gray-500 mt-1">{unavail.reason}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteUnavailability(unavail.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {past.length > 0 && (
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-2">Past Unavailability</h4>
            <div className="space-y-2">
              {past.slice(0, 3).map(unavail => (
                <div key={unavail.id} className="flex items-center justify-between p-2 border rounded-lg bg-gray-50">
                  <div>
                    <p className="text-sm">{formatDate(unavail.date)}</p>
                    <p className="text-xs text-gray-500">
                      {unavail.all_day ? 'All day' : `${formatTime(unavail.start_time)} - ${formatTime(unavail.end_time)}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StaffAvailabilityCalendar;